import React, { useState } from "react";
import {
  ImageListItem,
  ImageListItemBar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Button,
  TextField,
  Stack,
} from "@mui/material";
import { Link } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MuiAlert from "@mui/material/Alert";
import { useMutation, useQueryClient } from "react-query";
import AspectRatio from "@mui/joy/AspectRatio";
import { useLocationFile } from "../hooks/useFetch";
import axios from "axios";
import { Buffer } from "buffer";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import PromotedIcon from "./PromotedIcon";

export const onSuccess = (queryKey) => (data) => {
  console.log(`[Query: ${queryKey}] ✅ Success:`, data);
};

export const onError = (queryKey) => (error) => {
  console.error(`[Query: ${queryKey}] ❌ Error:`, error);
};

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function LocationListTile({
  token,
  name,
  address,
  locationId,
  imageFileName,
  isPromoted,
  active,
  homeItemType,
}) {
  const [dialogState, setDialogState] = useState({ open: false, text: "" });

  const pic = useLocationFile(
    onSuccess,
    onError,
    token.token,
    imageFileName,
    homeItemType
  );
  const queryClient = useQueryClient();

  // 🔁 Mappings based on itemType
  const config = {
    place: {
      queryKey: "places-get",
      editPath: `/editPlace/${locationId}`,
      deleteEndpoint: `/places/delete_place/${locationId}`,
      confirmationLabel: "place",
    },
    bigEvent: {
      queryKey: "big_events-get",
      editPath: `/editBigEvent/${locationId}`,
      deleteEndpoint: `/big_events/delete_big_event/${locationId}`,
      confirmationLabel: "event",
    },
  }[homeItemType];

  const deleteLocation = useMutation(
    () =>
      axios.delete(
        `${process.env.REACT_APP_BASE_URL}:${process.env.REACT_APP_ADMIN_PORT}${config.deleteEndpoint}`,
        {
          headers: {
            Accept: "*/*",
            token: token.token,
          },
        }
      ),
    {
      onSuccess: () => queryClient.invalidateQueries(config.queryKey),
    }
  );

  const handleTextChange = (event) => {
    setDialogState({ ...dialogState, text: event.target.value });
  };

  const handleClickOpen = () => {
    setDialogState({ open: true, text: "" });
  };

  const handleClose = () => {
    setDialogState({ open: false, text: "" });
  };

  const handleDelete = () => {
    handleClose();
    deleteLocation.mutate();
  };

  const confirmationText = `${name}/${locationId}/delete-this-${config.confirmationLabel}`;

  return (
    <div>
      <ImageListItem>
        <Link to={`/sections/${locationId}?homeItemType=${homeItemType}`}>
          <AspectRatio
            objectFit="cover"
            ratio="6/5"
            sx={{
              width: 300,
              bgcolor: "background.level2",
              borderRadius: 10,
            }}
          >
            {pic.isLoading ? (
              <Skeleton height="185px" highlightColor="#ffffff" />
            ) : pic.isError || !pic.data ? (
              <img
                style={{ opacity: active ? null : 0.5 }}
                src="/img/placeholder.png"
                alt="Cannot be displayed..."
              />
            ) : (
              <img
                style={{ opacity: active ? null : 0.5 }}
                src={`data:image/png;base64,${Buffer.from(pic.data).toString(
                  "base64"
                )}`}
                alt="Cannot be displayed..."
              />
            )}
          </AspectRatio>
        </Link>
        <ImageListItemBar
          title={
            <Stack direction="row" alignItems="center">
              <span>{name}</span>
              {isPromoted ? <PromotedIcon /> : null}
            </Stack>
          }
          sx={{
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
            backgroundColor: active ? null : "rgba(160, 15, 15, 0.7)",
          }}
          subtitle={address.el ?? address.en}
          actionIcon={
            <Stack direction="row" spacing={1}>
              <IconButton
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                aria-label={`edit ${name}`}
                component={Link}
                to={config.editPath}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                aria-label={`delete ${name}`}
                onClick={() =>
                  deleteLocation.isLoading ? null : handleClickOpen()
                }
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          }
        />
      </ImageListItem>
      <Dialog open={dialogState.open} onClose={handleClose}>
        <DialogTitle>Delete this {config.confirmationLabel}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Unexpected bad things will happen if you don’t read this!
          </DialogContentText>
          <DialogContentText>
            This action cannot be undone. This will permanently delete {name}{" "}
            event, groups and events associated with it. Please type{" "}
            {confirmationText}.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="delete-text"
            fullWidth
            variant="standard"
            onChange={handleTextChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleDelete}
            disabled={dialogState.text !== confirmationText}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={deleteLocation.isError}
        autoHideDuration={2000}
        onClose={() => deleteLocation.reset()}
      >
        <Alert
          onClose={() => deleteLocation.reset()}
          severity="error"
          sx={{ width: "100%" }}
        >
          Delete failed!
        </Alert>
      </Snackbar>
    </div>
  );
}
